import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import SplashScreenContext from 'contexts/SplashScreenContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppSelector } from 'hooks';
import { consumeOpenTablesIntent } from 'utils/auth';

// state.tables is plain Redux state — never persisted — so it's always empty
// on the post-login hard reload. utils/auth's captureOpenTablesBeforeLogin
// stashes every table a guest had open right before the login redirect —
// not just the active one, and not just when leaving from Multi-Table View
// (/grid). consumeLoginReturnPath() already lands the guest back on their
// active table's own URL (or /grid, or the lobby) via the hard reload; this
// component's job is only the OTHER tables that URL doesn't cover — e.g. a
// background table opened via the header's table switcher while multi-table
// view was off. It re-subscribes each one (one at a time, via the normal
// openTable path, forced through the reliable /table-route subscribe) then
// lands on whichever view the guest actually had before logging in.
const PER_TABLE_TIMEOUT_MS = 6000;

const PostLoginTablesRestore = () => {
  const [intent] = useState(consumeOpenTablesIntent);
  // The active table (if any) is already being subscribed via its own direct
  // URL landing — restoring it again here would just be a redundant no-op
  // navigate, so only the OTHER tables need this component's loop.
  const pending = useMemo(
    () => intent.tables.filter((t) => t.gameID !== intent.activeGameID),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(pending.length === 0);
  const tables = useAppSelector((state) => (state as any).tables) as Record<string, unknown>;
  const { openTable, openGrid, returnToLobby } = useContext(ViewManagerContext);
  const { showSplash, hideSplash } = useContext(SplashScreenContext);
  const requestedRef = useRef(false);
  const current = pending[index];

  // Only cover the screen while there's background work to do. The common
  // case — a guest with just their one active table open — has nothing left
  // for this component to restore (pending is empty), so it never shows the
  // splash and the guest's already-landed active table is never interrupted.
  const isRestoring = pending.length > 0 && !done;
  useEffect(() => {
    if (!isRestoring) return undefined;
    showSplash('postLoginTablesRestore');
    return () => hideSplash('postLoginTablesRestore');
  }, [isRestoring, showSplash, hideSplash]);

  useEffect(() => {
    if (done || !current) return;

    if (tables?.[current.gameID]) {
      requestedRef.current = false;
      if (index + 1 >= pending.length) {
        setDone(true);
      } else {
        setIndex(index + 1);
      }
      return;
    }

    if (requestedRef.current) return;
    requestedRef.current = true;
    // forceSingle: true — always route through /table (GameInner's real
    // joinTable subscribe), never ViewManagerContext's "already have a table
    // open → subscribe directly and jump straight to /grid" shortcut. That
    // shortcut exists for an interactive click while already viewing the
    // grid; it isn't proven for this bulk, back-to-back restore-on-reload
    // path, and the splash screen below already covers whichever table's
    // felt this call briefly flashes through mid-restore.
    openTable(current.gameID, current.gameType, 'no', { forceSingle: true });

    const timeout = setTimeout(() => {
      requestedRef.current = false;
      if (index + 1 >= pending.length) {
        setDone(true);
      } else {
        setIndex(index + 1);
      }
    }, PER_TABLE_TIMEOUT_MS);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, tables, done]);

  useEffect(() => {
    if (!done || pending.length === 0) return;
    // pending.length === 0 means there was nothing for this component to
    // restore in the first place (the guest's one active table already
    // landed on its own via the hard reload) — leave it exactly where it is.
    // Land back on whichever view the guest actually had before logging in.
    // Grid: none of the restored tables landed anywhere on their own (the
    // hard reload's own URL was /grid itself), so this is what finally shows
    // them. Single-table: the active table is already showing (it landed
    // there directly via the hard reload); this loop's own /table
    // navigations moved the URL through every OTHER table in turn, so it
    // must explicitly return to the one the guest actually had open. Lobby:
    // there was no active table to return to.
    if (intent.wasGridView) {
      openGrid();
    } else if (intent.activeGameID) {
      const active = intent.tables.find((t) => t.gameID === intent.activeGameID);
      if (active) openTable(active.gameID, active.gameType, 'no', { forceSingle: true });
    } else {
      returnToLobby();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  // This component never renders any visible UI of its own — see the
  // isRestoring effect above for the splash side-effect.
  return null;
};

export default PostLoginTablesRestore;
